<template>
  <div>
    <k-panel :hasContentToggle="false"
      title="Current Programmes"
      icon="fa-project-diagram"
    >
      <template #body>
        <section class="current-programmes-info">
          <div class="text">
            <p>To make changes to the <i class="highlight">content</i> of a programme,
            click on the link in the programme field in the table below. To change the
            <i class="highlight">name, description or other metadata</i> of a programme,
            click the edit button from the table</p>
          </div>
          <div class="archive-toggle">
            <k-toggle label="Show Archived" v-model="showArchived" :round="true" title="Check this box to display archived programmes"></k-toggle>
          </div>
        </section>
        <div id="programmes">
          <k-table
            :headers="kableHeaders"
            :rows="kableRows"
            :max="20"
            :hideable="true"
            :panel="false"
            @clicked="tableCallback"></k-table>
        </div>
      </template>
    </k-panel>
    <update-programme-entity
      :item="programmeToUpdate"
      :products="products"
      :itemType="undefined"
      @close="programmeToUpdate = undefined"
      @item-updated="getProgrammes"
      >
    </update-programme-entity>
  </div>
</template>

<style>
.current-programmes-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.current-programmes-info .text {
  width: 80%;
}

.archive-toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
}

#programmes > .k-table-container th {
  min-width: unset;
}

#programmes > .k-table-container .k-table-header-edit,
#programmes > .k-table-container .k-table-key-edit {
  text-align: center;
}

#programmes .k-table-header-programme,
#programmes .k-table-header-description,
#programmes .k-table-key-description,
#programmes .k-table-key-programme {
  min-width: 200px;
  white-space: unset;
}
</style>

<script>
import KPanel from '@base-components/k-panel.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import KToggle from '@base-components/forms/k-toggle.vue';
import PageReadyMixin from '@mixins/page-ready-mixin';
import { sortObjectArray } from '@utils/sort-by-object-property';
import UpdateProgrammeEntity from './update-programme-entity.vue';

const PROGRAMME_FEATURES = {
  kloud: { name: 'KLOUD' },
  chatbot: { name: 'Chatbot' },
  keyMaker: { name: 'Key Maker' },
  knowledgeBase: { name: 'Knowledge Base' },
};

export default {
  components: {
    KTable,
    KToggle,
    KPanel,
    UpdateProgrammeEntity,
  },

  mixins: [PageReadyMixin],

  props: {
    products: {
      type: Array,
    },
  },

  data() {
    return {
      programmes: [],
      programmesReady: false,
      showCreateProgramme: false,
      programmeToUpdate: undefined,
      showArchived: false,
      showSection: true,
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getProgrammes();
  },

  computed: {
    ready() {
      return this.programmesReady;
    },

    keyMakerEnabled() {
      return this.$featureFlags.isEnabled('keymaker-app-page', 'current-programmes');
    },

    kableHeaders() {
      return {
        edit: {
          name: 'Edit',
          type: 'button',
          options: {
            preset: 'edit-unstyled',
            title: row => `Edit ${row.name || 'programme'}`,
            ariaLabel: row => `Edit ${row.name || 'programme'}`,
          },
        },
        programme: {
          name: 'Programme',
          filterable: true,
          type: 'url',
        },
        description: {
          name: 'Description',
        },
        includeInReport: {
          name: 'Include in report',
        },
        product: {
          name: 'Product',
        },
        startDate: {
          name: 'Start Date',
          type: 'date',
          sortable: true,
        },
        plannedEndDate: {
          name: 'Planned End Date',
          type: 'date',
          sortable: true,
        },
        knowledgeBase: {
          name: PROGRAMME_FEATURES.knowledgeBase.name,
          type: 'button',
          options: {
            preset: row => (row.knowledgeBase ? 'disable' : 'enable'),
            title: row => this.programmeEnabledLabel(row, 'knowledgeBase', row.knowledgeBase),
            ariaLabel: row => this.programmeEnabledLabel(row, 'knowledgeBase', row.knowledgeBase),
          },
          sortable: true,
        },
        kloud: {
          name: PROGRAMME_FEATURES.kloud.name,
          type: 'button',
          options: {
            preset: row => (row.kloud ? 'disable' : 'enable'),
            title: row => this.programmeEnabledLabel(row, 'kloud', row.kloud),
            ariaLabel: row => this.programmeEnabledLabel(row, 'kloud', row.kloud),
          },
          sortable: true,
        },
        chatbot: {
          name: PROGRAMME_FEATURES.chatbot.name,
          type: 'button',
          options: {
            preset: row => (row.chatbot ? 'disable' : 'enable'),
            title: row => this.programmeEnabledLabel(row, 'chatbot', row.chatbot),
            ariaLabel: row => this.programmeEnabledLabel(row, 'chatbot', row.chatbot),
          },
          sortable: true,
        },
        ...(this.keyMakerEnabled ? {
          keyMaker: {
            name: PROGRAMME_FEATURES.keyMaker.name,
            type: 'button',
            options: {
              preset: row => (row.keyMaker ? 'disable' : 'enable'),
              title: row => this.programmeEnabledLabel(row, 'keyMaker', row.keyMaker),
              ariaLabel: row => this.programmeEnabledLabel(row, 'keyMaker', row.keyMaker),
            },
            sortable: true,
          },
        } : {}),
        archive: {
          name: 'Archive',
          type: 'button',
          options: {
            preset: row => (row.is_active ? 'archive' : 'unarchive'),
            title: row => this.programmeArchiveLabel(row),
            ariaLabel: row => this.programmeArchiveLabel(row),
          },
          sortable: true,
        },
      };
    },

    kableRows() {
      return sortObjectArray(this.programmes, 'date_created', true).map(val => ({
        programme: {
          text: val.name,
          path: { name: 'manage_programme', params: { programmeId: val.id } },
        },
        programmeId: val.id,
        creationDate: val.date_created,
        description: val.description || 'No description found',
        includeInReport: val.include_in_reporting ? 'yes' : 'no',
        product: this.products.find(p => p.id === val.product_id)?.name,
        startDate: val.start_date,
        plannedEndDate: val.planned_end_date,
        is_active: val.is_active,
        kloud: val.kloud_enabled,
        chatbot: val.chatbot_enabled,
        ...(this.keyMakerEnabled ? { keyMaker: val.key_maker_enabled } : {}),
        knowledgeBase: val.knowledge_base_enabled,
        name: val.name,
      })).filter(row => (this.showArchived ? true : row.is_active));
    },
  },

  methods: {
    getProgrammes() {
      this.$logger.info('Getting programmes');
      this.programmesReady = false;
      return this.$http.get('/api/curriculum/programmes?active_only=false').then(res => {
        this.programmes = res.data.programmes;
        this.$logger.info('Successfully retrieved programmes');
      }).catch(err => {
        this.$logger.error('Error retrieving programmes', undefined, err);
        this.$ktoast.showError(err);
      }).then(() => {
        this.programmesReady = true;
      });
    },

    archiveProgramme(programmeId) {
      this.$logger.info('Archiving programme', { programmeId }, true);
      this.$Loading.start();
      this.$http.delete(`/api/curriculum/programmes/${programmeId}/archive`)
        .then(() => {
          this.$logger.info('Successfully archived programme', { programmeId }, true);
          this.$ktoast.success('Programme has been archived');
          this.getProgrammes();
        }).catch(err => {
          this.$logger.error('Error archiving programme', { programmeId }, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    unarchiveProgramme(programmeId) {
      this.$logger.info('Unarchiving programme', { programmeId }, true);
      this.$Loading.start();
      this.$http.put(`/api/curriculum/programmes/${programmeId}/unarchive`)
        .then(() => {
          this.$logger.info('Successfully un-archived programme', { programmeId }, true);
          this.$ktoast.success('Programme has been un-archived');
          this.getProgrammes();
        }).catch(err => {
          this.$logger.error('Error unarchiving programme', { programmeId }, err);
          this.$ktoast.showError(err);
        }).then(() => {
          this.$Loading.finish();
        });
    },

    toastArchiveCallback(row) {
      if (row.is_active) {
        this.archiveProgramme(row.programmeId);
      } else {
        this.unarchiveProgramme(row.programmeId);
      }
    },

    getProgrammeArchiveToastMessage(row) {
      if (row.is_active) {
        return 'Archived programmes cannot be accessed on KATE.';
      }
      return 'Unarchived programmes are accessible on KATE.';
    },

    tableArchiveCallback(row) {
      this.$ktoast.confirmToast(this.getProgrammeArchiveToastMessage(row), 'warning', () => this.toastArchiveCallback(row), undefined);
    },

    setProgrammeFeature(programmeId, featureKey, currentlyEnabled) {
      const { name } = PROGRAMME_FEATURES[featureKey];
      const urlSlug = name.toLowerCase().replace(' ', '-');
      const action = currentlyEnabled ? 'disable' : 'enable';
      const actionPresent = currentlyEnabled ? 'Disabling' : 'Enabling';
      const actionPast = currentlyEnabled ? 'Disabled' : 'Enabled';

      this.$logger.info(`${actionPresent} ${name} for programme`, { programmeId }, true);
      return this.$http.put(`/api/curriculum/programmes/${programmeId}/${action}-${urlSlug}`)
        .then(() => {
          this.$logger.info(`${actionPast} ${name} for programme`, { programmeId });
          this.$ktoast.success(`${name} ${action}d`);
          this.getProgrammes();
        }).catch(err => {
          this.$logger.error(`Error ${action}ing ${name} for programme`, { programmeId }, err);
          this.$ktoast.showError(err);
        });
    },

    getProgrammeFeatureToastMessage(row, featureKey) {
      const { name } = PROGRAMME_FEATURES[featureKey];
      if (row[featureKey]) {
        return `${name} will be disabled for every module and assignment.`;
      }
      return `${name} will be enabled for every module and assignment.`;
    },

    toastFeatureCallback(row, featureKey) {
      this.setProgrammeFeature(row.programmeId, featureKey, row[featureKey]);
    },

    tableFeatureCallback(row, featureKey) {
      this.$ktoast.confirmToast(
        this.getProgrammeFeatureToastMessage(row, featureKey),
        'warning',
        () => this.toastFeatureCallback(row, featureKey),
        undefined,
      );
    },

    tableCallback(key, index, row, column) {
      if (column === 'edit') {
        this.openUpdateProgrammeModal(row.programmeId);
      } else if (column === 'archive') {
        this.tableArchiveCallback(row);
      } else if (PROGRAMME_FEATURES[column]) {
        this.tableFeatureCallback(row, column);
      }
    },

    programmeArchiveLabel(row) {
      if (row.is_active) {
        return `Archive ${row.name || 'programme'}.`;
      }
      return `Unarchive ${row.name || 'programme'}.`;
    },

    programmeEnabledLabel(row, featureKey, enabled) {
      const { name } = PROGRAMME_FEATURES[featureKey];
      return `${enabled ? 'Disable' : 'Enable'} ${name} for ${row.name || 'programme'}.`;
    },

    openUpdateProgrammeModal(programmeId) {
      const prog = this.programmes.find(x => x.id === programmeId);
      if (prog && typeof prog === 'object') {
        this.programmeToUpdate = JSON.parse(JSON.stringify(prog));
      } else {
        this.programmeToUpdate = undefined;
      }
    },
  },
};
</script>
